import { Collapse } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { QueryResult } from "molecules/queryResult/QueryResult";
import { useGetErDocumentationQuery } from "src/common/service/fusionHub/fusionHubApi.ts";
import type { ErNote } from "src/common/service/fusionHub/types.ts";
import { Card, CardHeader } from "src/features/planning/components/Card.tsx";
import { useLocale } from "src/locales/useLocale.ts";
import { useState } from "react";
import { SourceSystemLogoByName } from "./table/SourceSystemLogo.tsx";

import { IconCaretDown, IconCaretUp } from "@tabler/icons-react";

import { useTranslation } from "react-i18next";
type Props = {
	sourceSystemName: string;
	notes: string[];
};

const SourceDocumentation = ({ sourceSystemName, notes }: Props) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { t } = useTranslation();

	if (notes.length === 0) {
		return null;
	}

	return (
		<Card>
			<div className="flex items-center justify-between border-b px-4 py-2">
				<SourceSystemLogoByName sourceSystemName={sourceSystemName} />
				<Button
					onClick={() => setIsExpanded((expanded) => !expanded)}
					icon={isExpanded ? <IconCaretUp /> : <IconCaretDown />}
					variant="ghost"
				>
					{isExpanded ? t("Show less") : t("Show more")}
				</Button>
			</div>
			<Collapse in={isExpanded}>
				<div className="flex flex-col gap-2 p-4">
					{notes.map((note) => (
						<div key={note}>{note}</div>
					))}
				</div>
			</Collapse>
		</Card>
	);
};

export const Documentation = () => {
	const { lang } = useLocale();

	const getTranslation = (note: ErNote) => (lang === "sv" ? note.sv : note.en);

	return (
		<QueryResult
			query={useGetErDocumentationQuery()}
			render={(documentation) => {
				return (
					<div className="flex flex-col gap-4">
						{Object.entries(documentation).map(([name, part]) => {
							return (
								<Card key={name}>
									<CardHeader>
										<div className="text-lg">{getTranslation(part.title)}</div>
									</CardHeader>
									<div className="flex flex-col gap-4 p-4">
										<div className="flex flex-col gap-2">
											{part.notes.map((note) => (
												<div key={note.en}>{getTranslation(note)}</div>
											))}
										</div>
										{Object.entries(part.sources).map(
											([sourceSystemName, sourceDocumentation]) => {
												return (
													<SourceDocumentation
														key={sourceSystemName}
														sourceSystemName={sourceSystemName}
														notes={sourceDocumentation.notes.map(
															getTranslation,
														)}
													/>
												);
											},
										)}
									</div>
								</Card>
							);
						})}
					</div>
				);
			}}
		/>
	);
};
